'use client'

import type { FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import FilterBar from '@/components/blog/filterBar'
import ClientSearch from '@/components/blog/clientSearch'
import type { BlogPostMeta } from '@/lib/blog'

type Pagination = {
  currentPage: number
  totalPages: number
  totalPosts: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

type Props = {
  categories: string[]
  tags: string[]
  serverPosts: BlogPostMeta[] // initial ISR payload (page 1)
  pagination: Pagination // initial ISR pagination (page 1)
}

/**
 * Parse current URL search params (works both on mount and after popstate).
 */
function parseParams() {
  if (typeof window === 'undefined')
    return {
      page: 1,
      category: null as string | null,
      tag: null as string | null,
      search: '',
    }
  const sp = new URLSearchParams(window.location.search)
  return {
    page: Number(sp.get('page') ?? '1') || 1,
    category: sp.get('category'),
    tag: sp.get('tag'),
    search: sp.get('search') ?? '',
  }
}

const BlogClient: FC<Props> = ({
  categories,
  tags,
  serverPosts,
  pagination,
}) => {
  // ---------- local state (no router.push → no server re-render) ----------
  const sp = useSearchParams() // only used for initial hydration
  const [page, setPage] = useState(() => Number(sp.get('page') ?? '1') || 1)
  const [activeCategory, setActiveCategory] = useState<string | null>(() =>
    sp.get('category'),
  )
  const [activeTag, setActiveTag] = useState<string | null>(() => sp.get('tag'))
  const [searchQuery, setSearchQuery] = useState(() => sp.get('search') ?? '')

  const [listPosts, setListPosts] = useState<BlogPostMeta[]>(serverPosts)
  const [listPagination, setListPagination] = useState<Pagination>(pagination)
  const [isFetching, setIsFetching] = useState(false)

  const perPage = Math.max(serverPosts.length, 6)
  const isInitialMount = useRef(true)

  // ---------- sync URL (pushState - no navigation, just URL bar) ----------
  const syncUrl = useCallback(
    (p: number, cat: string | null, tag: string | null) => {
      const qs = new URLSearchParams()
      if (p > 1) qs.set('page', String(p))
      if (cat) qs.set('category', cat)
      if (tag) qs.set('tag', tag)
      const search = qs.toString()
      const url = search
        ? `${window.location.pathname}?${search}`
        : window.location.pathname
      window.history.pushState({}, '', url)
    },
    [],
  )

  // ---------- browser back/forward ----------
  useEffect(() => {
    const onPopState = () => {
      const { page: p, category, tag } = parseParams()
      setPage(p)
      setActiveCategory(category)
      setActiveTag(tag)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  // ---------- fetch posts when page/filter changes ----------
  useEffect(() => {
    // skip fetch on initial mount - we already have ISR data for page 1 with no filters
    if (isInitialMount.current) {
      isInitialMount.current = false
      // only fetch if URL has non-default params (deep-linked with filters/page)
      if (page === 1 && !activeCategory && !activeTag) return
    }

    let cancelled = false
    setIsFetching(true)

    const qs = new URLSearchParams()
    qs.set('page', String(page))
    qs.set('perPage', String(perPage))
    if (activeCategory) qs.set('category', activeCategory)
    if (activeTag) qs.set('tag', activeTag)
    ;(async () => {
      try {
        const res = await fetch(`/api/blog-list?${qs.toString()}`, {
          headers: { accept: 'application/json' },
        })
        if (!res.ok || cancelled) return
        const json: { posts: BlogPostMeta[]; pagination: Pagination } =
          await res.json()
        if (!cancelled) {
          setListPosts(json.posts)
          setListPagination(json.pagination)
        }
      } catch {
        // swallow - keep last good list
      } finally {
        if (!cancelled) setIsFetching(false)
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeCategory, activeTag])

  // ---------- callbacks for children ----------
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage)
      syncUrl(newPage, activeCategory, activeTag)
      // smooth scroll to top of blog grid
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [activeCategory, activeTag, syncUrl],
  )

  const handleFilterChange = useCallback(
    (cat: string | null, tag: string | null) => {
      setActiveCategory(cat)
      setActiveTag(tag)
      setPage(1) // reset to page 1 on filter change
      syncUrl(1, cat, tag)
    },
    [syncUrl],
  )

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <FilterBar
        categories={categories}
        tags={tags}
        activeCategory={activeCategory}
        activeTag={activeTag}
        onFilterChange={handleFilterChange}
      />

      <ClientSearch
        serverPosts={listPosts}
        page={listPagination.currentPage}
        pagination={listPagination}
        activeCategory={activeCategory}
        activeTag={activeTag}
        initialQuery={searchQuery}
        pending={isFetching}
        onPageChange={handlePageChange}
      />
    </div>
  )
}

export default BlogClient
